/**
 * The only module that touches the DOM for sign-in state.
 *
 * Mirrors `view.ts`: an interface the controller can be tested against with a
 * plain object, and one class that does the actual mutation.
 */

import type { SessionUser } from "./contract.js";

export type AuthState =
  /** Before the first `/api/session` answer. Without it the page flashes blank. */
  | { readonly kind: "checking" }
  | { readonly kind: "signedOut"; readonly message?: string }
  | { readonly kind: "signedIn"; readonly user: SessionUser };

export interface AuthView {
  render(state: AuthState): void;
  /** Where the Google button should be drawn. */
  signInContainer(): HTMLElement;
  onSignOut(handler: () => void): void;
}

export type AuthElements = {
  readonly signedOut: HTMLElement;
  readonly signedIn: HTMLElement;
  readonly googleButton: HTMLElement;
  readonly badge: HTMLElement;
  readonly name: HTMLElement;
  readonly avatar: HTMLImageElement;
  readonly signOutButton: HTMLButtonElement;
  readonly status: HTMLElement;
};

const MISSING_ELEMENT = (id: string): Error =>
  new Error(`Expected an element with id '${id}' in the document`);

/**
 * Resolves the elements the auth view needs.
 *
 * @throws {Error} when the template is missing one, so a broken page fails at
 * startup rather than leaving an unusable sign-in screen.
 */
export function resolveAuthElements(document: Document): AuthElements {
  const signedOut = document.getElementById("app-signed-out");
  const signedIn = document.getElementById("app-signed-in");
  const googleButton = document.getElementById("google-button");
  const badge = document.getElementById("auth-badge");
  const name = document.getElementById("auth-name");
  const avatar = document.getElementById("auth-avatar");
  const signOutButton = document.getElementById("sign-out-button");
  const status = document.getElementById("auth-status");

  if (signedOut === null) throw MISSING_ELEMENT("app-signed-out");
  if (signedIn === null) throw MISSING_ELEMENT("app-signed-in");
  if (googleButton === null) throw MISSING_ELEMENT("google-button");
  if (badge === null) throw MISSING_ELEMENT("auth-badge");
  if (name === null) throw MISSING_ELEMENT("auth-name");
  if (avatar === null) throw MISSING_ELEMENT("auth-avatar");
  if (signOutButton === null) throw MISSING_ELEMENT("sign-out-button");
  if (status === null) throw MISSING_ELEMENT("auth-status");

  return {
    signedOut,
    signedIn,
    googleButton,
    badge,
    name,
    avatar: avatar as HTMLImageElement,
    signOutButton: signOutButton as HTMLButtonElement,
    status,
  };
}

export class DomAuthView implements AuthView {
  constructor(private readonly elements: AuthElements) {}

  signInContainer(): HTMLElement {
    return this.elements.googleButton;
  }

  onSignOut(handler: () => void): void {
    this.elements.signOutButton.addEventListener("click", () => {
      handler();
    });
  }

  render(state: AuthState): void {
    const { signedOut, signedIn, badge, name, avatar, status } = this.elements;

    switch (state.kind) {
      case "checking":
        signedOut.hidden = true;
        signedIn.hidden = true;
        badge.hidden = true;
        status.textContent = "";
        status.dataset.tone = "neutral";
        return;
      case "signedOut":
        signedOut.hidden = false;
        signedIn.hidden = true;
        badge.hidden = true;
        status.textContent = state.message ?? "";
        status.dataset.tone = state.message === undefined ? "neutral" : "error";
        return;
      case "signedIn":
        signedOut.hidden = true;
        signedIn.hidden = false;
        badge.hidden = false;
        status.textContent = "";
        status.dataset.tone = "neutral";
        // textContent, never innerHTML: a display name is whatever the account
        // holder typed into Google, and is not ours to trust as markup.
        name.textContent = state.user.name === "" ? state.user.email : (
          state.user.name
        );
        avatar.src = state.user.picture;
        avatar.alt = "";
        avatar.hidden = state.user.picture === "";
        return;
    }
  }
}
